<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import EntityPickerPanel from 'src/components/shared/EntityPickerPanel.vue';
import { CONTRACT_PERIODS, CONTRACT_TYPES, type ContractFormData } from 'src/composables/useContractManagement';
import type { FetchOrganisationResponseLite } from 'src/api/models/fetchOrganisationResponseLite';
import type { FetchUnitResponseLite } from 'src/api/models/fetchUnitResponseLite';
import type { FetchAddressResponse } from 'src/api/models/fetchAddressResponse';
import type { NestedOrganisationContactPerson } from 'src/api/models/nestedOrganisationContactPerson';
import { toLocalDateString } from 'src/utils/date-utc';
import { ENTITY_THEMES } from 'src/components/ticket-edit/entity-display';
import {
  createContractOrganisationConfig,
  createContractUnitConfig,
  createContractBranchConfig,
  createContractContactConfig,
} from 'src/composables/contract-entity-configs';

export interface InitialEntities {
  customer?: FetchOrganisationResponseLite | null;
  supplier?: FetchOrganisationResponseLite | null;
  contact?: NestedOrganisationContactPerson | null;
  location?: FetchAddressResponse | null;
  unit?: FetchUnitResponseLite | null;
}

const props = defineProps<{
  modelValue: ContractFormData;
  readonly?: boolean;
  readonlyOrg?: boolean;
  initialEntities?: InitialEntities;
  errors?: Record<string, string>;
  searchOrganisationsFn: (query: string, page: number) => Promise<{ data: FetchOrganisationResponseLite[]; page: number; total_pages: number }>;
  searchUnitsFn: (orgId: string, query: string) => Promise<{ data: FetchUnitResponseLite[]; page: number; total_pages: number }>;
  searchBranchesFn: (orgId: string | null, query: string, page: number) => Promise<{ data: FetchAddressResponse[]; page: number; total_pages: number }>;
  searchContactPersonsFn: (orgId: string, query: string, page: number) => Promise<{ data: NestedOrganisationContactPerson[]; page: number; total_pages: number }>;
}>();

const orgDisabled = computed(() => props.readonly || props.readonlyOrg);

const emit = defineEmits<{
  'update:modelValue': [value: ContractFormData];
}>();

const selectedCustomer = ref<FetchOrganisationResponseLite | null>(null);
const selectedSupplier = ref<FetchOrganisationResponseLite | null>(null);
const selectedContact = ref<NestedOrganisationContactPerson | null>(null);
const selectedLocation = ref<FetchAddressResponse | null>(null);
const selectedUnit = ref<FetchUnitResponseLite | null>(null);

const form = computed(() => props.modelValue);

function update(field: keyof ContractFormData, value: unknown) {
  emit('update:modelValue', { ...props.modelValue, [field]: value });
}

const hasCustomer = computed(() => !!form.value.contract_holder_id);
const hasSupplier = computed(() => !!form.value.operator_id);
const hasStartDate = computed(() => !!form.value.start_date);

const customerIdRef = computed(() => form.value.contract_holder_id || undefined);
const supplierIdRef = computed(() => form.value.operator_id || undefined);
const customerNameRef = computed(() => selectedCustomer.value?.name);
const supplierNameRef = computed(() => selectedSupplier.value?.name);

const customerConfig = createContractOrganisationConfig(props.searchOrganisationsFn);
const supplierConfig = createContractOrganisationConfig(props.searchOrganisationsFn);
const unitConfig = createContractUnitConfig(customerIdRef, props.searchUnitsFn);
const branchConfig = createContractBranchConfig(supplierIdRef, props.searchBranchesFn);
const contactConfig = createContractContactConfig(customerIdRef, props.searchContactPersonsFn);

const endDateDisplay = computed(() => {
  if (!form.value.start_date) return '';
  if (form.value.period === null || form.value.period === 'indefinite') return '';
  const start = new Date(form.value.start_date);
  start.setMonth(start.getMonth() + Number(form.value.period));
  return start.toLocaleDateString('nl-NL');
});

function onStartDateChange(value: Date | null) {
  const next = { ...props.modelValue, start_date: value ? toLocalDateString(value) : null };
  if (!value) {
    next.running_hour_bat_start = null;
  }
  emit('update:modelValue', next);
}

function onCustomerSelect(entity: unknown) {
  const val = entity as FetchOrganisationResponseLite;
  selectedCustomer.value = val;
  const newId = val?.id ?? null;
  if (newId !== form.value.contract_holder_id) {
    emit('update:modelValue', {
      ...props.modelValue,
      contract_holder_id: newId ?? '',
      unit_id: '',
      contact_id: null,
    });
    selectedUnit.value = null;
    selectedContact.value = null;
  }
}

function onCustomerClear() {
  selectedCustomer.value = null;
  emit('update:modelValue', {
    ...props.modelValue,
    contract_holder_id: '',
    unit_id: '',
    contact_id: null,
  });
  selectedUnit.value = null;
  selectedContact.value = null;
}

function onSupplierSelect(entity: unknown) {
  const val = entity as FetchOrganisationResponseLite;
  selectedSupplier.value = val;
  const newId = val?.id ?? null;
  if (newId !== form.value.operator_id) {
    emit('update:modelValue', {
      ...props.modelValue,
      operator_id: newId ?? '',
      location_id: null,
    });
    selectedLocation.value = null;
  }
}

function onSupplierClear() {
  selectedSupplier.value = null;
  emit('update:modelValue', {
    ...props.modelValue,
    operator_id: '',
    location_id: null,
  });
  selectedLocation.value = null;
}

function onContactSelect(entity: unknown) {
  const val = entity as NestedOrganisationContactPerson;
  selectedContact.value = val;
  update('contact_id', val?.id ?? null);
}

function onContactClear() {
  selectedContact.value = null;
  update('contact_id', null);
}

function onLocationSelect(entity: unknown) {
  const val = entity as FetchAddressResponse;
  selectedLocation.value = val;
  update('location_id', val?.id ?? null);
}

function onLocationClear() {
  selectedLocation.value = null;
  update('location_id', null);
}

function onUnitSelect(entity: unknown) {
  const val = entity as FetchUnitResponseLite;
  selectedUnit.value = val;
  update('unit_id', val?.id ?? '');
}

function onUnitClear() {
  selectedUnit.value = null;
  update('unit_id', '');
}

watch(() => props.initialEntities, (entities) => {
  if (!entities) return;
  if (entities.customer) selectedCustomer.value = entities.customer;
  if (entities.supplier) selectedSupplier.value = entities.supplier;
  if (entities.contact) selectedContact.value = entities.contact;
  if (entities.location) selectedLocation.value = entities.location;
  if (entities.unit) selectedUnit.value = entities.unit;
}, { immediate: true });

watch(() => form.value.contract_holder_id, (newVal) => {
  if (!newVal) {
    selectedCustomer.value = null;
    selectedUnit.value = null;
    selectedContact.value = null;
  }
});

watch(() => form.value.operator_id, (newVal) => {
  if (!newVal) {
    selectedSupplier.value = null;
    selectedLocation.value = null;
  }
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <section class="flex flex-col gap-3">
      <h4 class="text-sm font-semibold text-color-secondary uppercase tracking-wide border-b border-surface-200 dark:border-surface-700 pb-2">
        Contractant
      </h4>
      <div class="grid grid-cols-2 gap-x-4 gap-y-3">
        <div class="flex flex-col gap-1">
          <EntityPickerPanel
            data-testid="contract-contractant"
            label="Contractant *"
            :theme="ENTITY_THEMES.organisation"
            :config="customerConfig"
            :model-value="selectedCustomer ?? undefined"
            :disabled="orgDisabled"
            :invalid="!!errors?.customer"
            @select="onCustomerSelect"
            @clear="onCustomerClear"
          />
          <small v-if="errors?.customer" class="text-red-500">{{ errors.customer }}</small>
        </div>

        <div class="flex flex-col gap-1">
          <EntityPickerPanel
            data-testid="contract-contact"
            label="Contact opdrachtgever"
            :theme="ENTITY_THEMES.orgContact"
            :config="contactConfig"
            :model-value="selectedContact ?? undefined"
            :parent-id="customerIdRef"
            :parent-name="customerNameRef"
            :disabled="orgDisabled || !hasCustomer"
            @select="onContactSelect"
            @clear="onContactClear"
          />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium">Referentie (PO-nummer)</label>
          <InputText
            data-testid="contract-reference"
            :model-value="form.reference_number"
            placeholder="Max. 100 tekens"
            :maxlength="100"
            :disabled="readonly"
            size="small"
            @update:model-value="update('reference_number', $event)"
          />
        </div>

        <div class="flex flex-col gap-1">
          <EntityPickerPanel
            data-testid="contract-unit"
            label="Unit *"
            :theme="ENTITY_THEMES.unit"
            :config="unitConfig"
            :model-value="selectedUnit ?? undefined"
            :parent-id="customerIdRef"
            :parent-name="customerNameRef"
            :disabled="orgDisabled || !hasCustomer"
            :invalid="!!errors?.unit"
            @select="onUnitSelect"
            @clear="onUnitClear"
          />
          <small v-if="errors?.unit" class="text-red-500">{{ errors.unit }}</small>
        </div>
      </div>
    </section>

    <section class="flex flex-col gap-3">
      <h4 class="text-sm font-semibold text-color-secondary uppercase tracking-wide border-b border-surface-200 dark:border-surface-700 pb-2">
        Locatie
      </h4>
      <div class="grid grid-cols-2 gap-x-4 gap-y-3">
        <div class="flex flex-col gap-1">
          <EntityPickerPanel
            data-testid="contract-gebruiker"
            label="Locatie naam *"
            :theme="ENTITY_THEMES.organisation"
            :config="supplierConfig"
            :model-value="selectedSupplier ?? undefined"
            :disabled="orgDisabled"
            :invalid="!!errors?.supplier"
            @select="onSupplierSelect"
            @clear="onSupplierClear"
          />
          <small v-if="errors?.supplier" class="text-red-500">{{ errors.supplier }}</small>
        </div>

        <div class="flex flex-col gap-1">
          <EntityPickerPanel
            data-testid="contract-locatie"
            label="Locatie adres *"
            :theme="ENTITY_THEMES.site"
            :config="branchConfig"
            :model-value="selectedLocation ?? undefined"
            :parent-id="supplierIdRef"
            :parent-name="supplierNameRef"
            :disabled="orgDisabled || !hasSupplier"
            :invalid="!!errors?.location"
            @select="onLocationSelect"
            @clear="onLocationClear"
          />
          <small v-if="errors?.location" class="text-red-500">{{ errors.location }}</small>
        </div>
      </div>
    </section>

    <section class="flex flex-col gap-3">
      <h4 class="text-sm font-semibold text-color-secondary uppercase tracking-wide border-b border-surface-200 dark:border-surface-700 pb-2">
        Contract
      </h4>
      <div class="grid grid-cols-2 gap-x-4 gap-y-3">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium">Contracttype *</label>
          <Select
            data-testid="contract-type"
            :model-value="form.type"
            :options="CONTRACT_TYPES"
            option-label="label"
            option-value="value"
            placeholder="Selecteer type"
            :disabled="readonly"
            :invalid="!!errors?.type"
            @update:model-value="update('type', $event)"
          />
          <small v-if="errors?.type" class="text-red-500">{{ errors.type }}</small>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium">Startdatum</label>
          <DatePicker
            data-testid="contract-start-date"
            :model-value="form.start_date ? new Date(form.start_date) : null"
            date-format="dd-mm-yy"
            placeholder="Selecteer datum"
            :disabled="readonly"
            :invalid="!!errors?.start_date"
            show-icon
            show-button-bar
            @update:model-value="onStartDateChange($event as Date | null)"
          />
          <small v-if="errors?.start_date" class="text-red-500">{{ errors.start_date }}</small>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium">Contractperiode</label>
          <Select
            data-testid="contract-period"
            :model-value="form.period"
            :options="CONTRACT_PERIODS"
            option-label="label"
            option-value="value"
            placeholder="Selecteer periode"
            :disabled="readonly"
            :invalid="!!errors?.period"
            @update:model-value="update('period', $event)"
            show-clear
          />
          <small v-if="errors?.period" class="text-red-500">{{ errors.period }}</small>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium">Einddatum</label>
          <InputText
            data-testid="contract-end-date"
            :model-value="endDateDisplay || 'Wordt berekend'"
            disabled
            size="small"
          />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium">Draaiuren bij start</label>
          <InputNumber
            data-testid="contract-running-hour-bat-start"
            :model-value="form.running_hour_bat_start"
            :min="0"
            :max-fraction-digits="0"
            locale="NL-nl"
            placeholder="Aantal uren"
            :disabled="readonly || !hasStartDate"
            :invalid="!!errors?.running_hour_bat_start"
            size="small"
            @update:model-value="update('running_hour_bat_start', $event)"
          />
          <small v-if="errors?.running_hour_bat_start" class="text-red-500">{{ errors.running_hour_bat_start }}</small>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium">Jaarlijkse prijsaanpassing</label>
          <Checkbox
            data-testid="annual-price-adjustment"
            :model-value="form.annual_price_adjustment"
            binary
            :disabled="readonly"
            @update:model-value="update('annual_price_adjustment', $event)"
          />
        </div>
      </div>
    </section>
  </div>
</template>
